// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/** An institution of the advisory proposal, with the IBANs under advice. */
export interface AdvisoryProposalInstitution {
  readonly institution_id: string;
  readonly products: {
    readonly accounts: ReadonlyArray<{ readonly iban: string }>;
  };
}

/** A client institution with the accounts the client shares. */
export interface SharedInstitutionAccounts {
  readonly institution_id: string;
  readonly accounts: ReadonlyArray<{ readonly iban: string }>;
}

/**
 * The advised accounts: the client institutions restricted to the IBANs the
 * advisory proposal covers, whatever the custodian.
 *
 * An institution of the proposal the client no longer shares is left out, since
 * there is no account data for it.
 *
 * @param proposalInstitutions - Institutions of the advisory proposal.
 * @param sharedInstitutions - Client institutions with their accounts.
 * @returns The shared institutions, with only the accounts under advice.
 */
export const buildAdvisoryProposalAccounts = <T extends SharedInstitutionAccounts>(
  proposalInstitutions:
    | ReadonlyArray<AdvisoryProposalInstitution>
    | null
    | undefined,
  sharedInstitutions: readonly T[],
): T[] =>
  (proposalInstitutions ?? []).flatMap(institution => {
    const shared = sharedInstitutions.find(
      ({ institution_id }) => institution_id === institution.institution_id,
    );

    if (!shared) {
      return [];
    }

    const advisedIbans = new Set(
      institution.products.accounts.map(({ iban }) => iban),
    );

    return [
      {
        ...shared,
        accounts: shared.accounts.filter(({ iban }) => advisedIbans.has(iban)),
      },
    ];
  });

/** The IBANs of the advised accounts. */
export const advisoryProposalIbans = (
  institutions: readonly SharedInstitutionAccounts[],
): string[] =>
  institutions.flatMap(({ accounts }) => accounts.map(({ iban }) => iban));
